import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Spinner,
  useColorMode,
  useDisclosure,
} from '@chakra-ui/react';
import { useMediaQuery } from '@chakra-ui/react';
import { documentAction, taskAction } from 'actions';
import configs from 'configs';
import { HTTP_CODE_RESPONSE, TEMPORARY } from 'helpers/constant';
import { addTokenURL, getFileExtension } from 'helpers/file';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { UploadService } from 'services/upload/upload.service';
import { Task, TaskFileDocument } from 'types/reducers/task';
import { getStorageReadBlobToken } from 'utils/blob';
import { v4 as uuidv4 } from 'uuid';

import Chat from './Chat';
import FilePreviewDialog, { PreviewFileState } from './FilePreviewDialog';
import SelectFiles from './SelectFiles';
import SelectFilesMobile from './SelectFilesMobile';
import TasksList from './TasksList';
import TasksListMobile from './TasksListMobile';

const Summary = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { tasks, processingStreamingChunkStatusTask } = useSelector((state) => state.task);
  const [toggleSidebarMobile, setToggleSidebarMobile] = useState(true);

  const [isLoading, setIsLoading] = useState(false);
  const [isShowingPreviewFile, setIsShowingPreviewFile] = useState(false);
  const [previewFile, setPreviewFile] = useState<PreviewFileState>({});
  const { isOpen, onOpen: onOpenSidebarMobile, onClose: onCloseSidebarMobile } = useDisclosure();
  const { colorMode } = useColorMode();
  const bgColor = { light: 'white.500', dark: '#424242' };
  const [isLarger460] = useMediaQuery('(min-width: 30em)');

  const loadData = () => {
    setIsLoading(true);
    dispatch(taskAction.resetData());
    dispatch(taskAction.getTasks());
    setIsLoading(false);
  };

  const onChangeTask = (item: Task) => {
    if (processingStreamingChunkStatusTask) return;
    dispatch(
      taskAction.updateCurrentTask({
        task_id: item?.id,
        type: item?.type,
      })
    );
  };

  const newTask = useMemo(
    () => ({
      id: uuidv4(),
      type: TEMPORARY,
      name: t('common.addNew'),
      last_updated: new Date().toISOString(),
    }),
    [t]
  );

  const addNewTask = () => {
    if (processingStreamingChunkStatusTask) return;

    const temporaryTask = tasks.data.find((task) => task.type === TEMPORARY);
    if (temporaryTask) {
      dispatch(
        taskAction.updateCurrentTask({
          task_id: temporaryTask.id,
          type: temporaryTask?.type,
        })
      );
      return;
    }

    dispatch(taskAction.addNewTask(newTask));
    dispatch(taskAction.clearTemporaryTask());
    dispatch(taskAction.updateTemporaryTaskId(newTask.id));
    onChangeTask(newTask);
  };

  const checkReadTokenValid = (token: string | null) => {
    if (!token) {
      return false;
    }
    const searchParams = new URLSearchParams(token);
    const expiredTime = new Date(searchParams.get('se') as string).getTime();
    const currentTime = new Date().getTime();
    const offsetRefreshTime = configs.setting.TIMEOUT_REFRESH_SAS_TOKEN_IN_MS;

    return expiredTime > currentTime + offsetRefreshTime;
  };

  const onOpenFile = (file: TaskFileDocument) => {
    readFile(file);
  };

  const readFile = (file: TaskFileDocument) => {
    if (!checkReadTokenValid(getStorageReadBlobToken())) {
      dispatch(documentAction.getReadToken());
    }
    fetchData(file);
  };

  const fetchData = (file: TaskFileDocument) => {
    const fileUrl = addTokenURL(file?.url, getStorageReadBlobToken());
    setPreviewFile({
      url: fileUrl,
      name: file?.name,
      type: getFileExtension(file?.url),
    });

    (async () => {
      try {
        const res = await UploadService.fetchHeadFile(fileUrl);
        if (
          res.statusText !== 'OK' &&
          (res.status === HTTP_CODE_RESPONSE.UNAUTHORIZED ||
            res.status === HTTP_CODE_RESPONSE.NO_SAS_TOKEN)
        ) {
          readFile(file);
        } else {
          setIsShowingPreviewFile(true);
        }
      } catch (error) {
        console.log(error);
      }
    })();
  };

  const onClose = () => {
    setIsShowingPreviewFile(false);
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    onCloseSidebarMobile();
  }, [isLarger460]);
  return (
    <Box h={'calc(100vh - 150px)'}>
      <FilePreviewDialog isOpen={isShowingPreviewFile} file={previewFile} onClose={onClose} />
      {isLoading ? (
        <Box w='100vw' h='calc(100vh - 150px)' bg='bg_main_container'>
          <Spinner />
        </Box>
      ) : (
        <>
          {isLarger460 && (
            <Flex
              justify='center'
              flex={1}
              gap={4}
              display={{ base: 'none', xl: 'flex' }}
              h='calc(100vh - 150px)'
            >
              <TasksList onAddNew={addNewTask} onChange={onChangeTask} />
              <SelectFiles onOpenFile={onOpenFile} />
              <Chat onAddNew={addNewTask} onOpenSidebarMobile={onOpenSidebarMobile} />
            </Flex>
          )}

          <Box
            justifyContent={'center'}
            display={{ base: 'flex', xl: 'none' }}
            h={'calc(100vh - 150px)'}
          >
            <Chat onAddNew={addNewTask} onOpenSidebarMobile={onOpenSidebarMobile} />
            <Drawer isOpen={isOpen} onClose={onCloseSidebarMobile} placement='left'>
              <DrawerOverlay />
              <DrawerContent bgColor={bgColor[colorMode]}>
                <DrawerCloseButton />
                <DrawerHeader gap={1} display={'flex'}>
                  <Button onClick={() => setToggleSidebarMobile(true)}>
                    {t('summary.taskList')}
                  </Button>
                  <Button onClick={() => setToggleSidebarMobile(false)}>
                    {t('summary.selectFile')}
                  </Button>
                </DrawerHeader>
                <DrawerBody>
                  <Box display={`${toggleSidebarMobile ? 'block' : 'none'}`}>
                    <TasksListMobile
                      onAddNew={addNewTask}
                      onChange={onChangeTask}
                      onCloseSidebarMobile={onCloseSidebarMobile}
                    />
                  </Box>
                  <Box display={`${toggleSidebarMobile ? 'none' : 'block'}`}>
                    <SelectFilesMobile onOpenFile={onOpenFile} />
                  </Box>
                </DrawerBody>
              </DrawerContent>
            </Drawer>
          </Box>
        </>
      )}
    </Box>
  );
};

export default Summary;
